import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { BarChart3, TrendingUp, Coins, Info, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/format";
import { useChartColors } from "@/components/finance/chart";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { OverviewTab } from "./financials/OverviewTab";
import { ResultTab } from "./financials/ResultTab";
import { LiquidityTab } from "./financials/LiquidityTab";
import { DividendsTab } from "./financials/DividendsTab";
import { AccountingConnection } from "./financials/AccountingConnection";

const CHAIR_ROLES = ["Styreleder", "Nestleder", "Daglig leder"];

export default function Financials() {
  const { company } = useOutletContext();
  const { user } = useAuth();
  const { companyId } = useParams();
  const colors = useChartColors();
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  const [view, setView] = useState("chart");
  const [reloadKey, setReloadKey] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    setData(null);
    const q = year ? `?year=${year}` : "";
    api.get(`/companies/${companyId}/financials${q}`)
      .then((r) => { setData(r.data); if (year === null) setYear(r.data.year); })
      .catch(() => setData({}));
  }, [companyId, year, reloadKey]);

  const months = useMemo(() => data?.months || [], [data]);
  const totals = data?.totals || {};
  const source = data?.source || {};
  const provider = source.provider;
  const connected = !!source.connected;
  const live = !!source.live;
  const updatedAt = source.fetched_at || source.updated_at;

  const reload = () => setReloadKey((k) => k + 1);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const q = year ? `?year=${year}&refresh=true` : "?refresh=true";
      const { data: d } = await api.get(`/companies/${companyId}/financials${q}`);
      setData(d);
      if (d?.source?.error) toast.error(d.source.error);
      else toast.success(`Regnskapstall oppdatert fra ${d?.source?.provider || "regnskapssystemet"}`);
    } catch {
      toast.error("Kunne ikke oppdatere regnskapstall akkurat nå");
    } finally {
      setSyncing(false);
    }
  };

  const barData = useMemo(() => months.map((m) => ({
    month: m.month, Inntekter: m.inntekter, Kostnader: m.kostnader, Resultat: m.resultat,
  })), [months]);

  return (
    <div className="mx-auto max-w-6xl" data-testid="financials-page">
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Nøkkeltall</p>
          <h1 className="mt-2 font-headings text-2xl font-extrabold tracking-tight sm:text-3xl">{company?.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {live ? (
              <span data-testid="source-badge-live" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-3 w-3" /> Fra {provider}
              </span>
            ) : connected ? (
              <span data-testid="source-badge-error" className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                <Info className="h-3 w-3" /> {provider} tilkoblet · viser demodata
              </span>
            ) : (
              <span data-testid="source-badge-demo" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2.5 py-0.5 text-xs font-medium">
                <Info className="h-3 w-3" /> Demodata
              </span>
            )}
            {!connected && provider ? (
              <span className="text-xs">Klar for Tripletex, Fiken og PowerOffice</span>
            ) : null}
            {updatedAt ? (
              <span className="text-xs" data-testid="financials-updated-at">· Sist synkronisert {formatDateTime(updatedAt)}</span>
            ) : null}
            {connected ? (
              <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={syncNow} disabled={syncing} data-testid="financials-sync-now">
                <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> Oppdater nå
              </Button>
            ) : null}
          </div>
        </div>
        <AccountingConnection
          companyId={companyId}
          companyName={company?.name}
          source={source}
          provider={provider}
          connected={connected}
          dataLoaded={!!data}
          onReload={reload}
        />
      </div>

      <Tabs defaultValue="oversikt" className="w-full">
        <TabsList data-testid="financials-tabs">
          <TabsTrigger value="oversikt" data-testid="tab-oversikt"><BarChart3 className="mr-1.5 h-4 w-4" /> Oversikt</TabsTrigger>
          <TabsTrigger value="resultat" data-testid="tab-resultat"><TrendingUp className="mr-1.5 h-4 w-4" /> Resultat/balanse</TabsTrigger>
          <TabsTrigger value="likviditet" data-testid="tab-likviditet"><Coins className="mr-1.5 h-4 w-4" /> Likviditet</TabsTrigger>
          <TabsTrigger value="utbytte" data-testid="tab-utbytte"><Coins className="mr-1.5 h-4 w-4" /> Utbytte</TabsTrigger>
        </TabsList>

        <TabsContent value="oversikt" className="mt-6">
          <OverviewTab data={data} live={live} />
        </TabsContent>

        <TabsContent value="resultat" className="mt-6">
          <ResultTab data={data} months={months} totals={totals} barData={barData} year={year} setYear={setYear} view={view} setView={setView} colors={colors} />
        </TabsContent>

        <TabsContent value="likviditet" className="mt-6">
          <LiquidityTab data={data} colors={colors} />
        </TabsContent>

        <TabsContent value="utbytte" className="mt-6">
          <DividendsTab
            companyId={companyId}
            resultat={typeof totals.resultat === "number" ? totals.resultat : undefined}
            canManage={!!user?.is_admin || (company?.my_roles || []).some((r) => CHAIR_ROLES.includes(r))}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
